import * as THREE from 'three';
import themeSets from '../Configs/theme-sets.js';
import { themeNames } from '../Configs/theme-sets.js';

const THEME_SETS = themeSets;
/** @typedef {keyof typeof THEME_SETS} ThemeName */
/** @typedef {keyof typeof THEME_SETS[ThemeName]} ThemeColorKey */

const DEFAULT_THEME = Object.values(THEME_SETS)[0];
const DEFAULT_THEME_NAME = Object.keys(THEME_SETS)[0];

let themeName = DEFAULT_THEME_NAME;
let currentTheme = DEFAULT_THEME;

/** 
 * threeTheme takes the hexcodes of `currentTheme` and assigns them to `THREE.Colors` (with the same key name as currentTheme)
 * @type {Record<ThemeColorKey, THREE.Color>} 
 * */
const threeTheme = {};
let eventBus;
/**
 * @param {ThemeName} themeName - The name of the theme to activate
 * @returns {ReturnType<typeof getCurrentTheme>} The active theme
 */
export function init(_eventBus, _themeName) {
    eventBus = _eventBus;
    themeName = _themeName;
    assignToPage(themeName);
    initThreeTheme();
    assignToThree(themeName);
    return getCurrentTheme();
}
/**
 * @param {ThemeName} themeName - The name of the theme to activate
 * @returns {ReturnType<typeof getCurrentTheme>} The active theme
 */
export function assignToPage(themeName) {
    currentTheme = THEME_SETS[themeName] || Object.values(THEME_SETS)[0];
    // Update DaisyUI theme so non-threejs gets updated too 
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', themeName);
    }
    eventBus.pushLocal('themeChange', currentTheme);

    return getCurrentTheme();
}

function initThreeTheme() {
    for (const key in currentTheme) {
        threeTheme[key] = new THREE.Color();
        // it is important we mutate the colors instead of creating new ones on this line
        // that way we can update them later directly in memory
        threeTheme[key].set(currentTheme[key]);
    }
    return threeTheme
}
/**
 * @param {ThemeName} themeName - The name of the theme to activate
 * @returns {ReturnType<typeof threeTheme>} The active theme
 */
export function assignToThree(themeName) {
    if (Object.keys(threeTheme).length === 0) throw new Error('threeTheme not initialized. Call initThreeTheme before assignThemeToThree');
    currentTheme = THEME_SETS[themeName] || Object.values(THEME_SETS)[0];
    //TODO: strip transparent values from hexkeys    
    for (const key in currentTheme) {
        // this alone will already update all colors in threejs 
        // as all colors point to threeTheme objects in memory
        threeTheme[key].set(currentTheme[key]);
    }
    return threeTheme
}

/**
 * @param {ThemeName} themeName - The name of the theme to activate
 */
export function assignToPageAndThree(themeName) {
    assignToPage(themeName);
    assignToThree(themeName);
}

export function getCurrentTheme() {
    return currentTheme;
}

/** YAML files will use strings to assign colors to assets */
export function getThreeColorFromString(value, fallback) {
    const isString = typeof value === 'string';
    const themeColor = isString ? threeTheme[value] : undefined;

    if (themeColor) return themeColor;

    const parsedColor = toThreeColor(value);
    if (parsedColor) return parsedColor;

    const fallbackThemeColor = typeof fallback === 'string' ? threeTheme[fallback] : undefined;
    if (fallbackThemeColor) return fallbackThemeColor;

    return toThreeColor(fallback) || new THREE.Color('#161616');
}

function toThreeColor(value) {
    if (value instanceof THREE.Color) return value;
    if (value === undefined || value === null || value === '') return null;

    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (trimmedValue === '') return null;

        const looksLikeColorLiteral =
            trimmedValue.startsWith('#') ||
            trimmedValue.startsWith('rgb') ||
            trimmedValue.startsWith('hsl') ||
            /^[0-9a-f]{3}$/i.test(trimmedValue) ||
            /^[0-9a-f]{6}$/i.test(trimmedValue);

        if (!looksLikeColorLiteral) return null;

        return new THREE.Color(trimmedValue);
    }

    if (typeof value === 'number') {
        return new THREE.Color(value);
    }

    return null;
}

/** YAML files will use strings to assign colors to assets */
export function getHexcolorFromString(value, fallback) {
    const isString = typeof value === 'string';
    const themeValue = isString ? currentTheme[value] : undefined;

    if (themeValue) return themeValue;
    if (value) return value;

    return fallback;
}


export function getThemeName() {
    return themeName;
}

export function listThemes() {
    return Object.keys(THEME_SETS);
}

export default threeTheme;

export const theme = {
    DEFAULT_THEME,
    DEFAULT_THEME_NAME,
    themeNames,
    init,
    assignToPage,
    assignToThree,
    assignToPageAndThree,
    getCurrentTheme,
    getThemeName,
    listThemes
};
